import { FormatTimes } from "../types/crowdhandler";
import { CookieEntry } from "../utils/cookieStorage";

const formatTimes = (estimatedWait: number): FormatTimes => {
    let values: FormatTimes = {
        // always set 1 minute as as default
        total_minutes: estimatedWait < 2 ? 2 : estimatedWait,
        hours: null,
        minutes: estimatedWait < 2 ? 2 : estimatedWait,
    };

    if (estimatedWait === Infinity || isNaN(estimatedWait)) {
        return values;
    } else {
        // If it's less that 1 set to null. Backwards compatibility. Estimate wait time can be < 0
        // If it's 2 or lower set to 2, so never showing less than 2 minutes (do not have to deal with single/plural)
        // If it's higher then 90 divide by 60 and round up to nearest integer, update hours and hide minutes
        // If it's 90 or lower then hide hours and update minutes.
        const convertTotalMinutesToHoursAndMinutes = (total_minutes: number): { hours: number, minutes: number } => {
            const hours = Math.floor(total_minutes / 60);
            const minutes = total_minutes % 60;
            return { hours, minutes };
        }

        if (estimatedWait < 1) {
            values.total_minutes = null;
        } else if (estimatedWait <= 2) {
            values.minutes = 2;
            values.hours = null;
        } else if (estimatedWait > 60) {
            const { hours, minutes } = convertTotalMinutesToHoursAndMinutes(estimatedWait);
            values.hours = hours;
            values.minutes = minutes;
        }
    }

    return values;
};

const calculateEstimate = (
    {
        mostRecentEstimate,
        mostRecentPosition,
        firstRecorded,
        firstPosition,
        samples
    }: CookieEntry,
    currentPosition: number,
    serverWait?: number | null
): FormatTimes => {

    if (firstPosition === null || currentPosition === null) {
        return formatTimes(serverWait ?? 0);
    }

    if (mostRecentEstimate && currentPosition === mostRecentPosition) {
        return formatTimes(mostRecentEstimate);
    }

    // Rate is in positions/min and is the divisor of the final estimate
    // (estimate = position / rate), so however we measure it directly drives the
    // result. We measure it as a float (no integer rounding, which previously
    // made the rate coarse — and occasionally 0 -> Infinity — on small movement)
    // and over *real* fractional minutes (no whole-minute rounding or `+1`
    // fudge, which used to inflate the denominator and so the wait by up to ~4x
    // early in a session). See eta-ab.mjs.
    //
    // Preferred source is a trailing window (the caller prunes `samples` to
    // SAMPLE_WINDOW_MS): the rate across the oldest and newest in-window samples.
    // This tracks the *current* pace rather than the whole-session average — a
    // slow early stretch would otherwise drag the estimate ~25% above reality
    // for the rest of the wait. A multi-minute window keeps it from whipsawing
    // on a single noisy poll or a burst of timeout expiries. See
    // eta-window-sweep.mjs for the window-length sweep behind the 8-minute pick.
    let rateOfChange: number | null = null;
    if (Array.isArray(samples) && samples.length >= 2) {
        const oldest = samples[0];
        const newest = samples[samples.length - 1];
        const spanMinutes = (newest.t - oldest.t) / 60000;
        const movedInWindow = oldest.pos - newest.pos;
        if (spanMinutes >= 0.25 && movedInWindow > 0) {
            rateOfChange = movedInWindow / spanMinutes;
        }
    }

    // Fall back to the since-arrival average until the window holds a usable
    // span (the first poll or two), then to the server estimate if even that
    // lacks signal — rather than dividing by ~0.
    if (rateOfChange === null) {
        const elapsedMinutes = (Date.now() - firstRecorded) / 60000;
        const positionChange = firstPosition - currentPosition;
        if (elapsedMinutes < 0.25 || positionChange <= 0) {
            return formatTimes(serverWait ?? 0);
        }
        rateOfChange = positionChange / elapsedMinutes;
    }

    // This observed rate intentionally reflects *actual* advancement, which in a
    // long queue includes sessions ahead expiring on the timeout (rolling, not a
    // cliff) as well as admission out the front. That makes the wait drop below
    // `position / admissionRate` when abandonment is high — which is correct, so
    // we trust it rather than clamping it back toward the server `wait`.
    let estimatedWait = Math.round(currentPosition / rateOfChange);

    // Can't work with an estimate of 0
    if (estimatedWait < 1) {
        estimatedWait = 1;
    }

    return formatTimes(estimatedWait)

};

export { calculateEstimate }